import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import TextWithIcon from '../../components/TextWithIcon';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import TopicBreadcrumbs from '../../topic/TopicBreadcrumbs';
import TopicDetailPreviewPopOver from '../../topic/TopicDetailPreviewPopOver';
import TopicNameWithIcon from '../../topic/TopicNameWithIcon';
import { isFeatureEnabled } from '../../utils/features';
import { Link } from '../../utils/router/v3Bridge';
import topicPath from '../../utils/url/topicPath';
import useIsPublicView from '../../utils/useIsPublicView';
import getRemoveTopicDisabledMessage from '../sharing/getRemoveTopicDisabledMessage';
import styles from './PostTopicWithBreadcrumbs.module.scss';

const TOPIC_FRAGMENT = gql(`
  fragment PostTopicWithBreadcrumbs_topic on PublicTopicFields {
    id
    name
    ancestors {
      ...TopicBreadcrumbs_topics
    }
    ...getRemoveTopicDisabledMessage_topic
    ...topicPath_topic
    ...TopicNameWithIcon_topic
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_FRAGMENT>;

const POST_FRAGMENT = gql(`
  fragment PostTopicWithBreadcrumbs_post on Post {
    id
    ...getRemoveTopicDisabledMessage_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

type PostTopicWithBreadcrumbsProps = {
  onRemove?: (topic: TopicFragment) => unknown;
  topic: TopicFragment;
  large?: boolean;
} & ({ editing?: false; post?: never } | { editing: true; post: PostFragment });

const PostTopicWithBreadcrumbs = ({
  topic,
  onRemove,
  editing,
  post,
  large,
}: PostTopicWithBreadcrumbsProps) => {
  const user = useAppSelector((state) => state.session.user);
  const isPublicView = useIsPublicView();
  const isRemoveButtonHidden = !editing || !onRemove;
  const hasNoAncestor = _.isEmpty(topic.ancestors);
  const removeDisabledMessage =
    editing && topic.__typename === 'Topic'
      ? getRemoveTopicDisabledMessage(topic, user, post)
      : undefined;

  const topicLinkContent = (
    <div
      className={classNames(styles.content, {
        [styles.hideRemoveButton]: isRemoveButtonHidden,
        [styles.large]: large,
        [styles.newPostLayout]: isFeatureEnabled('new_post_layout'),
      })}
    >
      {!hasNoAncestor && (
        <TopicBreadcrumbs
          link={isPublicView}
          truncate
          topics={topic.ancestors}
          size={large ? 'regular' : 'small'}
          showIcon
        />
      )}

      <div
        className={classNames(styles.topicName, {
          [styles.topicPublicView]: isPublicView,
        })}
      >
        {isPublicView ? (
          <TextWithIcon
            truncate="single"
            text={topic.name}
            to={topicPath(topic)}
          />
        ) : (
          <TopicNameWithIcon
            iconSize="medium"
            topic={topic}
            truncate="single"
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.popOverContainer}>
        {isPublicView ? (
          topicLinkContent
        ) : (
          <TopicDetailPreviewPopOver
            topicId={topic.id}
            placement="bottom-start"
          >
            <Link
              className={classNames(styles.link, {
                [styles.noAncestor]: hasNoAncestor,
              })}
              to={topicPath(topic)}
            >
              {topicLinkContent}
            </Link>
          </TopicDetailPreviewPopOver>
        )}
      </div>
      {!isRemoveButtonHidden && (
        <div className={styles.remove}>
          <Tooltip content={removeDisabledMessage}>
            <Button
              onClick={() => onRemove(topic)}
              block
              disabled={!!removeDisabledMessage}
              aria-label="Remove topic from post"
              className={styles.removeButton}
            >
              <SVGIcon size="small">{clearIcon}</SVGIcon>
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default PostTopicWithBreadcrumbs;
